/* Morgan & Reid — Cross-sell Recommendation Queue Engine (cart drawer).
 * PHASE 1: merchandising only. Every recommendation is FULL PRICE — no discounts, no Functions.
 * STANDALONE module. Not the free gift, not the milestone gift, not the bundle widget.
 *
 * ARCHITECTURE (follows free-gift.js v2 conventions):
 *  - FAIL-CLOSED CONFIG: /api/cross-sell is fetched ONCE per page load and cached in memory.
 *    {enabled:false}, a fetch error, or malformed JSON all mean DISABLED -> render nothing.
 *    There is NO fallback config baked into this file.
 *  - NO fetch/XHR monkeypatching EVER. We listen only to the theme's own post-render signals
 *    ('cart:updated', 'cart:refresh', pubsub cartUpdate) — the exact wiring free-gift.js uses —
 *    so we never race the drawer render.
 *  - QUEUE ENGINE, ONE AT A TIME: every cart render merges all triggered queues into one ordered
 *    list (per trigger line in cart order, items in priority order, de-duped by productId keeping
 *    first occurrence), walks it, and surfaces exactly ONE card — the first survivor. Three skip
 *    states: ADDED (cart already contains that productId), DISMISSED (sessionStorage map
 *    "mr_xsell_dismissed"), UNAVAILABLE (no variant with available:true). colourMatch items are
 *    additionally skipped when the trigger's colour and the item's colour are both known and
 *    differ. Queue exhausted or no trigger lines in cart -> the slot is emptied (hidden).
 *  - SESSION DISMISSAL: "x" writes productId into sessionStorage and advances the queue IN PLACE
 *    (pure re-render from cached config + last cart read — no cart write). Storage failures are
 *    swallowed: no sessionStorage = empty dismissal map, never an error.
 *  - Lines we add are tagged property _mr_cross_sell: "<queueId>" so reporting can attribute them.
 *    Trigger detection ignores lines tagged _free_gift, _mr_milestone_gift, or _mr_cross_sell.
 *  - DOM: renders ONLY inside #mr-xsell-slot (re-queried on every pass — the drawer's innerHTML is
 *    replaced on every cartUpdate). Slot absent -> do nothing. Never touches the drawer footer.
 *
 * SAFETY / KILL ORDER:
 *  1. Config flag first: enabled !== true -> this script renders NOTHING and writes NOTHING.
 *  2. This script NEVER adds or removes cart lines on its own. The ONLY cart write is the single
 *     /cart/add.js fired by the user clicking "Add" (guarded against double-clicks). There is no
 *     reconcile loop, no removal path, no safety net needed — full price by design.
 *  3. Display-only DOM writes + that one user-initiated add are the ONLY side effects.
 */
(function () {
  "use strict";

  var API_BASE = "https://commerce-command-79465365634.australia-southeast1.run.app";
  var SHOP = (window.Shopify && window.Shopify.shop) || "manchester-factory-australia.myshopify.com";
  var ROUTE_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  var SLOT_ID = "mr-xsell-slot";
  var XSELL_PROP = "_mr_cross_sell";
  var DISMISS_KEY = "mr_xsell_dismissed";

  var configPromise = null;   // in-memory config cache: fetch once per page load, reuse
  var lastCart = null;        // last /cart.js read, for in-place re-render on dismiss
  var scheduled = null;       // debounce timer
  var chain = Promise.resolve(); // serialize passes so two renders never overlap
  var adding = false;         // double-click guard on the Add button

  function j(url, opts) { return fetch(url, opts).then(function (r) { return r.ok ? r.json() : Promise.reject(r); }); }
  function getCart() { return j(ROUTE_ROOT + "cart.js"); }
  function num(v) { return String(v == null ? "" : v).replace(/\D/g, ""); }

  // Fail closed: any error, non-2xx, or missing enabled:true collapses to {enabled:false}.
  function getConfig() {
    if (!configPromise) {
      configPromise = j(API_BASE + "/api/cross-sell?shop=" + encodeURIComponent(SHOP))
        .then(function (cfg) { return (cfg && cfg.enabled === true && cfg.queues && cfg.queues.length) ? cfg : { enabled: false }; })
        .catch(function () { return { enabled: false }; });
    }
    return configPromise;
  }

  // ── Session dismissal map (productId -> true). Unavailable storage == empty map, never throws. ──
  function getDismissed() {
    try {
      var raw = window.sessionStorage.getItem(DISMISS_KEY);
      var map = raw ? JSON.parse(raw) : null;
      return (map && typeof map === "object") ? map : {};
    } catch (e) { return {}; }
  }
  function dismiss(productId) {
    try {
      var map = getDismissed();
      map[num(productId)] = true;
      window.sessionStorage.setItem(DISMISS_KEY, JSON.stringify(map));
    } catch (e) {}
  }

  function isTaggedLine(it) {
    var p = it.properties || {};
    return !!(p._free_gift || p._mr_milestone_gift || p[XSELL_PROP]);
  }

  // ── The queue engine. Pure function of (config, cart, dismissed) -> first surviving item or null. ──
  function pickRecommendation(cfg, cart) {
    var dismissed = getDismissed();

    // Every productId already in the cart counts as OWNED (any line, tagged or not — a line we
    // added via cross-sell is owned too, so it never gets re-recommended).
    var owned = {};
    var i, k;
    for (i = 0; i < cart.items.length; i++) owned[num(cart.items[i].product_id)] = true;

    // Trigger lines: untagged cart lines whose product_id appears in some queue's triggerProductIds.
    // MERGED QUEUE (Amazon model): for each trigger line in cart order, append that queue's items
    // in priority order; de-dupe by productId keeping the FIRST occurrence. Each merged entry
    // remembers the colour of the trigger line that pulled it in (for colourMatch).
    var merged = [];
    var seen = {};
    for (i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      if (isTaggedLine(line)) continue;
      var pid = num(line.product_id);
      for (var q = 0; q < cfg.queues.length; q++) {
        var queue = cfg.queues[q];
        var trig = queue.triggerProductIds || [];
        var hit = false;
        for (k = 0; k < trig.length; k++) if (num(trig[k]) === pid) { hit = true; break; }
        if (!hit) continue;
        var trigColour = (queue.triggerColours && queue.triggerColours[trig[k]] != null)
          ? queue.triggerColours[trig[k]] : null;
        var items = queue.items || [];
        for (k = 0; k < items.length; k++) {
          var ipid = num(items[k].productId);
          if (seen[ipid]) continue;
          seen[ipid] = true;
          merged.push({
            item: items[k], queueId: queue.id, triggerColour: trigColour,
            triggerTitle: queue.triggerTitle || null, triggerWarmth: queue.triggerWarmth || null, triggerType: queue.triggerType || null,
          });
        }
      }
    }

    // Walk: first item not ADDED, not DISMISSED, not UNAVAILABLE, not colour-mismatched.
    for (i = 0; i < merged.length; i++) {
      var it = merged[i].item;
      var ipid2 = num(it.productId);
      if (owned[ipid2]) continue;                                    // ADDED / owned
      if (dismissed[ipid2]) continue;                                // DISMISSED this session
      var avail = (it.variants || []).filter(function (v) { return v.available === true; });
      if (!avail.length) continue;                                   // UNAVAILABLE
      // colourMatch: only recommend when colours match or either colour is unknown.
      if (it.colourMatch && merged[i].triggerColour && it.colour && it.colour !== merged[i].triggerColour) continue;
      return {
        item: it, queueId: merged[i].queueId, available: avail,
        triggerTitle: merged[i].triggerTitle, triggerWarmth: merged[i].triggerWarmth, triggerType: merged[i].triggerType,
      };
    }
    return null;
  }

  // ── Rendering. Writes ONLY inside #mr-xsell-slot; slot absent -> no-op. ──
  function money(p) {
    var n = Number(p);
    if (isNaN(n)) return String(p);
    // Shopify-style integer cents unless the value already carries decimals.
    if (String(p).indexOf(".") === -1 && n === Math.floor(n) && n >= 100) n = n / 100;
    return "$" + n.toFixed(2);
  }

  // ── Display copy helpers (DISPLAY-ONLY — the real product/variant goes to cart unchanged).
  //    Title parsing is banned for MATCHING; shortening what we show is a pure display concern. ──
  function collapse(s) { return s.replace(/\s+/g, " ").trim(); }

  // "Morgan and Reid Cocoa Snuggle Fleece Comforter Set" -> "Cocoa Snuggle" (for reason lines).
  function shortTriggerName(title) {
    var t = String(title || "");
    t = t.replace("Morgan and Reid ", "").replace(" Comforter Set", "").replace(" Coverlet Set", "").replace(" Coverlet", "").replace(" Fleece", "");
    return collapse(t);
  }

  // Anchor noun from the queue's triggerType (additive API field). Missing/unrecognised ->
  // "comforter": old API shapes and comforter anchors render today's copy verbatim.
  function anchorNoun(triggerType) {
    return /coverlet/i.test(String(triggerType || "")) ? "coverlet" : "comforter";
  }

  // Range word for "Completes your X set" — from the trigger title; null when unknown.
  function rangeWord(title) {
    var t = String(title || "");
    if (/rabbit/i.test(t)) return "Rabbit Fur";
    if (/checkered/i.test(t)) return "Checkered";
    if (/velvet/i.test(t)) return "Velvet";
    if (/snuggle/i.test(t)) return "Snuggle";
    return null;
  }

  // "Cocoa Snuggle Fleece European Pillowcase Pair" -> "Cocoa European Pillowcases".
  // Conservative: strip brand + range noise, map the category suffix; anything unrecognised
  // falls back to the (de-branded) full title — never invents words.
  function shortDisplayTitle(title) {
    var t = String(title || "").replace("Morgan and Reid ", "");
    t = t.replace(" Snuggle Fleece ", " ").replace(" Velvet Crushed ", " Velvet ").replace(" Velvet Stripe ", " Velvet ");
    var SUFFIX = [
      ["European Pillowcase Pair", "European Pillowcases"],
      ["Pillowcase Pair", "Pillowcases"],
      ["Sheet Set", "Sheets"],
      ["Throw Rug", "Throw"],
    ];
    for (var i = 0; i < SUFFIX.length; i++) {
      var at = t.indexOf(SUFFIX[i][0]);
      if (at !== -1) { t = t.slice(0, at) + SUFFIX[i][1] + t.slice(at + SUFFIX[i][0].length); break; }
    }
    return collapse(t);
  }

  // Honest reason line, driven by the resolver source. Unknown source (stale cached API without
  // the field) -> empty string: no line at all rather than a claim we can't back.
  function reasonFor(pick) {
    var src = pick.item.source;
    var noun = anchorNoun(pick.triggerType);
    var Noun = noun === "coverlet" ? "Coverlet" : "Comforter";
    var trig = pick.triggerTitle ? shortTriggerName(pick.triggerTitle) : null;
    if (src === "colourLink") return trig ? "Perfectly matched to your " + trig + " " + Noun + "." : "Perfectly matched to your " + noun + ".";
    if (src === "warmth") {
      var w = String(pick.triggerWarmth || "");
      var season = (w === "Winter" || w === "Extra Warm") ? "winter" : w === "Summer" ? "summer" : w === "All Seasons" ? "all-seasons" : null;
      return season ? "Designed to pair with your " + season + " " + noun + "." : "Designed to pair with your " + noun + ".";
    }
    if (src === "rangeDefault") {
      // Storage bag: anchor-aware line — coverlet buyers may not own a comforter.
      if (/storage bag/i.test(String(pick.item.title || "")) && noun === "coverlet") return "Store your bedding neatly between seasons.";
      var rw = pick.triggerTitle ? rangeWord(pick.triggerTitle) : null;
      return rw ? "Completes your " + rw + " set." : "Completes your set.";
    }
    if (src === "override") return trig ? "Chosen for your " + trig + " " + Noun + "." : "Chosen for your cart.";
    return "";
  }

  // Source-aware CTA — same honesty rule as the reason line: "matching" ONLY on colour-linked
  // items, and only when the category noun derives cleanly; anything fragile or unknown falls
  // back to the plain "Add to cart" rather than a wrong noun. House style: verb-first, no
  // symbol prefixes; sentence case (secondary action — must not compete with Checkout below).
  function ctaFor(pick) {
    var src = pick.item.source;
    if (src === "warmth") return "Add the sheets";
    if (src === "colourLink") {
      var t = String(pick.item.title || "");
      if (/pillowcase/i.test(t)) return "Add matching pillowcases";
      if (/blanket/i.test(t)) return "Add matching blanket";
      if (/throw/i.test(t)) return "Add matching throw";
      if (/cushion/i.test(t)) return "Add matching cushion";
    }
    return "Add to cart";
  }

  var lastRenderedPid = null; // enter animation only when the CARD CONTENT changes, not on every drawer re-render

  function renderCard(slot, pick) {
    var item = pick.item, avail = pick.available;
    slot.innerHTML = "";

    var card = document.createElement("div");
    card.className = "mr-xsell-card";
    var pid = num(item.productId);
    if (pid !== lastRenderedPid) card.className += " mr-xsell-enter";
    lastRenderedPid = pid;

    var closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "mr-xsell-dismiss";
    closeBtn.setAttribute("aria-label", "Dismiss recommendation");
    closeBtn.textContent = "Not now";
    closeBtn.addEventListener("click", function () {
      dismiss(item.productId);
      renderFromCache(); // advance IN PLACE: no cart write, no fetch — cached config + last cart
    });
    card.appendChild(closeBtn);

    var label = document.createElement("div");
    label.className = "mr-xsell-label";
    label.textContent = "Complete the look";
    card.appendChild(label);

    var row = document.createElement("div");
    row.className = "mr-xsell-row";
    card.appendChild(row);

    var img = null;
    if (item.image) {
      img = document.createElement("img");
      img.className = "mr-xsell-image";
      img.src = item.image;
      img.alt = item.title || "";
      img.loading = "lazy";
      row.appendChild(img);
    }

    var info = document.createElement("div");
    info.className = "mr-xsell-info";
    row.appendChild(info);

    var title = document.createElement("div");
    title.className = "mr-xsell-title";
    title.textContent = shortDisplayTitle(item.title);
    info.appendChild(title);

    // Real Judge.me rating only — no element at all when the product has no review data.
    var rating = item.rating;
    if (rating && Number(rating.value) > 0 && Number(rating.count) > 0) {
      var ratingEl = document.createElement("div");
      ratingEl.className = "mr-xsell-rating";
      ratingEl.textContent = "★ " + Number(rating.value).toFixed(1) + " (" + Number(rating.count) + ")";
      info.appendChild(ratingEl);
    }

    var reason = reasonFor(pick);
    if (reason) {
      var reasonEl = document.createElement("div");
      reasonEl.className = "mr-xsell-reason";
      reasonEl.textContent = reason;
      info.appendChild(reasonEl);
    }

    var price = document.createElement("div");
    price.className = "mr-xsell-price";
    price.textContent = money(avail[0].price);
    info.appendChild(price);

    var select = null;
    if (avail.length > 1) {
      select = document.createElement("select");
      select.className = "mr-xsell-select";
      for (var i = 0; i < avail.length; i++) {
        var opt = document.createElement("option");
        opt.value = String(avail[i].id);
        opt.textContent = avail[i].title;
        select.appendChild(opt);
      }
      // Variant change updates BOTH price and image (variant image; product image as fallback).
      (function (sel, variants, priceEl, imgEl, fallbackSrc) {
        sel.addEventListener("change", function () {
          for (var v = 0; v < variants.length; v++) {
            if (String(variants[v].id) === sel.value) {
              priceEl.textContent = money(variants[v].price);
              if (imgEl) {
                var want = variants[v].image || fallbackSrc;
                if (want && imgEl.src !== want) imgEl.src = want;
              }
              break;
            }
          }
        });
      })(select, avail, price, img, item.image || null);
      info.appendChild(select);
    }

    var addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "mr-xsell-add";
    var ctaLabel = ctaFor(pick);
    addBtn.textContent = ctaLabel;
    addBtn.addEventListener("click", function () {
      var variantId = select ? select.value : String(avail[0].id);
      addToCart(variantId, pick.queueId, addBtn, ctaLabel);
    });
    card.appendChild(addBtn);

    slot.appendChild(card);
  }

  // One display pass: re-query the slot (drawer DOM is replaced on every cartUpdate), pick, render.
  function render(cfg, cart) {
    var slot = document.getElementById(SLOT_ID);
    if (!slot) return;
    if (!cfg || cfg.enabled !== true) return; // fail closed: render nothing, remove nothing
    var pick = pickRecommendation(cfg, cart);
    if (!pick) { slot.innerHTML = ""; lastRenderedPid = null; return; } // exhausted / no triggers -> hide entirely
    renderCard(slot, pick);
  }

  // Pure in-place re-render (dismiss path). No network.
  function renderFromCache() {
    if (!lastCart) return;
    getConfig().then(function (cfg) { render(cfg, lastCart); });
  }

  // ── The single user-initiated cart write, then the exact Redo-safe re-render from free-gift.js. ──
  // We fetch full cart + rendered sections via a no-op cart/update.js and publish through the
  // theme's NATIVE cartUpdate pubsub (NOT cart:refresh first — cart:refresh makes the Redo
  // package-protection app stack a duplicate toggle in the drawer footer).
  function refreshDrawer() {
    var sections = [];
    try { document.documentElement.dispatchEvent(new CustomEvent("cart:bundled-sections", { bubbles: true, detail: { sections: sections } })); } catch (e) {}
    var pubsub = window.theme && window.theme.pubsub;
    return j(ROUTE_ROOT + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ sections: sections, sections_url: window.location.pathname }),
    }).then(function (cart) {
      if (pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.cartUpdate && cart && cart.sections) {
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { source: "cross-sell", cart: cart });
      } else {
        try { document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: false } })); } catch (e) {}
      }
    }).catch(function () {});
  }

  function addToCart(variantId, queueId, btn, ctaLabel) {
    if (adding) return;
    adding = true;
    if (btn) { btn.disabled = true; btn.textContent = "Adding…"; }
    var properties = {};
    properties[XSELL_PROP] = String(queueId);
    j(ROUTE_ROOT + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: properties }] }),
    }).then(function () {
      // Added-state beat: show confirmation briefly, THEN run the normal Redo-safe drawer
      // re-render, which replaces the slot and reveals the next recommendation (its enter
      // animation covers the swap). Static DOM until then — nothing fights the re-render.
      if (btn) { btn.textContent = "✓ Added"; btn.classList.add("mr-xsell-added"); }
      return new Promise(function (res) { setTimeout(res, 700); }).then(refreshDrawer);
    }).catch(function (e) {
      if (window.console) console.log("[cross-sell] add error:", e);
      if (btn) { btn.disabled = false; btn.textContent = ctaLabel || "Add to cart"; btn.classList.remove("mr-xsell-added"); }
    }).then(function () { adding = false; });
  }

  // ── One pass per cart event: config (cached) + fresh /cart.js, then display-only render. ──
  function passOnce() {
    return getConfig().then(function (cfg) {
      if (cfg.enabled !== true) return; // disabled: never even read the cart
      return getCart().then(function (cart) {
        lastCart = cart;
        render(cfg, cart);
      });
    }).catch(function (e) { if (window.console) console.log("[cross-sell] pass error:", e); });
  }

  // Debounce bursts of cart events into one pass; append to the chain so passes never overlap.
  function schedule() {
    clearTimeout(scheduled);
    scheduled = setTimeout(function () {
      chain = chain.then(passOnce).catch(function () {});
    }, 250);
  }

  // Wire ONLY the theme's own cart signals. No global fetch/XHR interception.
  function wire() {
    // Post-render DOM event the theme dispatches after every cart mutation (cart.js onCartUpdate).
    document.addEventListener("cart:updated", schedule);
    // The PDP Bundle Builder signals ONLY with 'cart:refresh'; listen for it too. Safe: our own
    // refreshDrawer() may dispatch cart:refresh, but the resulting pass is display-only.
    document.addEventListener("cart:refresh", schedule);
    // Pubsub covers PDP add-to-cart where no cart-items element emits the DOM event.
    try {
      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS && theme.pubsub.PUB_SUB_EVENTS.cartUpdate) {
        theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function () { schedule(); });
      }
    } catch (e) {}
  }

  function init() { wire(); schedule(); } // initial pass covers landing with triggers already in cart
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
